import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import polyline from '@mapbox/polyline';
import 'leaflet/dist/leaflet.css';
import { api } from '../utils/api';

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Zoom the map to `coords`. Re-measures the container first: Leaflet caches the size it
// saw when the map was created, and a map that appears in a card still laying itself out
// (or after the page re-flows) would otherwise be fitted to the wrong size and stay at its
// starting view. Returns false when the container has no size yet, so the caller can try
// again once it does.
const fitToCoords = (map, coords) => {
  map.invalidateSize();
  const size = map.getSize();
  if (!size.x || !size.y) return false;
  const bounds = L.latLngBounds(coords);
  const spanLat = Math.abs(bounds.getNorth() - bounds.getSouth());
  const spanLng = Math.abs(bounds.getEast() - bounds.getWest());
  if (spanLat < 1e-5 && spanLng < 1e-5) {
    // One place (every marker on the same point, or a trip with no route). A bounds with
    // no size cannot be fitted, so centre on it at street level.
    map.setView(bounds.getCenter(), 15, { animate: false });
  } else {
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 16, animate: false });
  }
  return true;
};

const TripMap = ({ tripDetails, height = '400px', driverLocation = null, onRefreshDriver = null, onCancelledMarkerClick = null }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const routeLayerRef = useRef(null);
  const driverMarkerRef = useRef(null);
  // Which trip/route state the map was last fitted to — see the fit step below.
  const fittedForRef = useRef(null);
  // The latest points to fit to, which fit they belong to, and when the last fit ran, so a
  // resize shortly after can redo it.
  const lastCoordsRef = useRef([]);
  const fitKeyRef = useRef(null);
  const lastFitAtRef = useRef(0);

  // Check if we have location data
  const hasStartLocation = tripDetails?.startLocation;
  const hasEndLocation = tripDetails?.endLocation;
  const hasStops = tripDetails?.stops && tripDetails.stops.length > 0;
  const hasEncodedPolyline = tripDetails?.encodedPolyline && tripDetails.encodedPolyline.length > 0;
  const hasDriverLocation = driverLocation?.coordinates && driverLocation.coordinates.length === 2;
  const hasLocationData = hasStartLocation || hasEndLocation || hasStops || hasEncodedPolyline || hasDriverLocation;

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return undefined;

    // Initialize map
    const map = L.map(mapRef.current).setView([11.05, 77.0], 6);
    mapInstanceRef.current = map;
    // A new map starts at the default view, so nothing has been fitted to IT yet. The
    // "already fitted" memory belongs to a map, not to the component: React's development
    // mode (and a map element appearing late) builds a fresh map after one was already
    // fitted, and without this reset the new one was left zoomed out at the default view.
    fittedForRef.current = null;

    // Add OpenStreetMap tiles - simple configuration
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
      subdomains: ['a', 'b', 'c']
    }).addTo(map);

    // Create a layer group for markers and add to map
    markersLayerRef.current = L.layerGroup().addTo(map);

    // Keep the map in step with its container. Without this the size Leaflet measured at
    // creation is used for ever: a container that is laid out afterwards (a card that
    // renders late, a collapsed sidebar, a resized window) leaves the map mis-sized, and a
    // fit done before that is wrong. While a fit is fresh or has not worked yet, redo it.
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        const m = mapInstanceRef.current;
        if (!m) return;
        m.invalidateSize();
        const coords = lastCoordsRef.current;
        const settling = Date.now() - lastFitAtRef.current < 3000 || fittedForRef.current !== fitKeyRef.current;
        if (coords && coords.length > 0 && settling && fitToCoords(m, coords)) {
          fittedForRef.current = fitKeyRef.current;
          lastFitAtRef.current = Date.now();
        }
      });
      observer.observe(mapRef.current);
    }

    // Cleanup function
    return () => {
      if (observer) observer.disconnect();
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    // Depends on whether the map element exists: when the first render has nothing to show
    // (no route data yet) there is no element, and [mapRef] never re-ran once it appeared.
  }, [hasLocationData]);

  // Update markers and route whenever tripDetails changes (excluding driver location)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !markersLayerRef.current) return;

    // Clear previous markers and route
    markersLayerRef.current.clearLayers();
    if (routeLayerRef.current) {
      try { map.removeLayer(routeLayerRef.current); } catch (e) {}
      routeLayerRef.current = null;
    }
    // The driver marker is NOT touched here. It belongs to the driver-location effect below,
    // which runs only when the driver's position changes. Removing it from this effect
    // deleted it every time the trip data refreshed (and the live route refreshes every few
    // seconds), and nothing put it back until the driver next moved: the car vanished
    // from the head of the route.

    // Helper to create icon
    const createCustomIcon = (color, size = 20, content = '') => {
      return L.divIcon({
        html: content || `<div style="background-color: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
        className: 'custom-marker',
        iconSize: [size, size],
        iconAnchor: [Math.floor(size/2), Math.floor(size/2)]
      });
    };

    const coordinates = [];

    // Add start
    if (hasStartLocation && Array.isArray(tripDetails.startLocation)) {
      const [lng, lat] = tripDetails.startLocation;
      const startMarker = L.marker([lat, lng], { icon: createCustomIcon('#10B981') });
      startMarker.bindPopup(`<div class="text-center"><div class="font-bold text-green-600">Start Location</div><div class="text-sm">${lat.toFixed(6)}, ${lng.toFixed(6)}</div></div>`);
      markersLayerRef.current.addLayer(startMarker);
      coordinates.push([lat, lng]);
    }

    // Stops
    if (hasStops) {
      tripDetails.stops.forEach((stop) => {
        if (stop.location && Array.isArray(stop.location)) {
          const [lng, lat] = stop.location;
          let markerColor = '#F59E0B';
          if (stop.name === 'Pickup Point') markerColor = '#3B82F6';
          if (stop.name === 'Drop Point') markerColor = '#EF4444';
          const stopMarker = L.marker([lat, lng], { icon: createCustomIcon(markerColor) });
          const popupContent = `<div class="text-center"><div class="font-bold" style="color: ${markerColor}">${stop.name}</div><div class="text-sm text-gray-600">${stop.address || ''}</div>${stop.waitingTime > 0 ? `<div class="text-orange-600 text-xs">Wait: ${stop.waitingTime} mins</div>` : ''}</div>`;
          stopMarker.bindPopup(popupContent);
          markersLayerRef.current.addLayer(stopMarker);
          coordinates.push([lat, lng]);
        }
      });
    }

    // End
    if (hasEndLocation && Array.isArray(tripDetails.endLocation)) {
      const [lng, lat] = tripDetails.endLocation;
      const endMarker = L.marker([lat, lng], { icon: createCustomIcon('#EF4444') });
      endMarker.bindPopup(`<div class="text-center"><div class="font-bold text-red-600">End Location</div><div class="text-sm">${lat.toFixed(6)}, ${lng.toFixed(6)}</div></div>`);
      
      endMarker.on('click', async () => {
        try {
          endMarker.bindPopup(`<div class="text-center"><div class="font-bold text-red-600">End Location</div><div class="text-sm text-gray-500 mt-1">Fetching location name...</div><div class="text-xs text-gray-400 mt-1">${lat.toFixed(6)}, ${lng.toFixed(6)}</div></div>`).openPopup();
          
          const res = await api.post('/geocoding/reverse', {
            latitude: (lat),
            longitude: (lng)
          });
          const placeName = res?.data?.placeName || res?.placeName || res?.data?.data?.placeName || 'Location not found';
          endMarker.bindPopup(`<div class="text-center"><div class="font-bold text-red-600">End Location</div><div class="text-sm font-medium text-gray-900 mt-1">${placeName}</div><div class="text-xs text-gray-500 mt-1">${lat.toFixed(6)}, ${lng.toFixed(6)}</div></div>`).openPopup();
          if (onCancelledMarkerClick) {
            onCancelledMarkerClick(placeName);
          }
        } catch (err) {
          console.error('Reverse geocoding error:', err);
          endMarker.bindPopup(`<div class="text-center"><div class="font-bold text-red-600">End Location</div><div class="text-sm text-red-500 mt-1">Failed to load location</div><div class="text-xs text-gray-500 mt-1">${lat.toFixed(6)}, ${lng.toFixed(6)}</div></div>`).openPopup();
        }
      });

      markersLayerRef.current.addLayer(endMarker);
      coordinates.push([lat, lng]);
    }

    // Cancelled loc
    if (tripDetails.cancelledLoc && (tripDetails.status === 'CANCELLED' || tripDetails.status === 'DIVERGED')) {
      try {
        const cLat = Number(tripDetails.cancelledLoc.lat);
        const cLon = Number(tripDetails.cancelledLoc.lon);
        if (!Number.isNaN(cLat) && !Number.isNaN(cLon)) {
          const cancelIcon = L.divIcon({ html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;"><svg viewBox=\"0 0 24 24\" width=\"20\" height=\"20\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><circle cx=\"12\" cy=\"12\" r=\"10\" fill=\"#ffffff\" stroke=\"#ef4444\" stroke-width=\"2\" /><path d=\"M15 9L9 15M9 9l6 6\" stroke=\"#ef4444\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\" /></svg></div>`, className: 'cancel-marker', iconSize: [28, 28], iconAnchor: [14, 14] });
          const cancelMarker = L.marker([cLat, cLon], { icon: cancelIcon });
          cancelMarker.bindPopup(`<div class="text-center"><div class="font-bold text-red-600">Cancelled Here</div><div class="text-sm">${cLat.toFixed(6)}, ${cLon.toFixed(6)}</div></div>`);
          cancelMarker.on('click', async () => {
            try {
              const res = await api.post('/geocoding/reverse', {
                latitude: (cLat),
                longitude: (cLon)
              });
              const placeName = res?.data?.placeName || res?.placeName || 'Location not found';
              cancelMarker.bindPopup(`<div class="text-center"><div class="font-bold text-red-600">Cancelled Here</div><div class="text-sm font-medium text-gray-900 mt-1">${placeName}</div><div class="text-xs text-gray-500">${cLat.toFixed(6)}, ${cLon.toFixed(6)}</div></div>`).openPopup();
              if (onCancelledMarkerClick) {
                onCancelledMarkerClick(placeName);
              }
            } catch (err) {
              console.error('Reverse geocoding error:', err);
            }
          });
          markersLayerRef.current.addLayer(cancelMarker);
          coordinates.push([cLat, cLon]);
        }
      } catch (e) {
        // ignore
      }
    }

    // Polyline
    if (hasEncodedPolyline && tripDetails.encodedPolyline) {
      try {
        const decodedPoints = polyline.decode(tripDetails.encodedPolyline);
        let latLngs = decodedPoints;
        let alternativeCoords = null;
        try { alternativeCoords = polyline.decode(tripDetails.encodedPolyline, 5); } catch (e) {}
        const scaledCoords = latLngs.map(coord => [coord[0] / 10, coord[1] / 10]);
        const scaledValid = scaledCoords.filter(coord => coord[0] >= -90 && coord[0] <= 90 && coord[1] >= -180 && coord[1] <= 180);
        let coordsToUse = latLngs;
        if (scaledValid.length > latLngs.length * 0.8) coordsToUse = scaledCoords;
        else if (alternativeCoords && alternativeCoords.length > 0) {
          const altValid = alternativeCoords.filter(coord => coord[0] >= -90 && coord[0] <= 90 && coord[1] >= -180 && coord[1] <= 180);
          if (altValid.length > coordsToUse.length * 0.8) coordsToUse = alternativeCoords;
        }
        const finalValid = coordsToUse.filter(coord => Array.isArray(coord) && coord.length === 2 && typeof coord[0] === 'number' && typeof coord[1] === 'number' && !isNaN(coord[0]) && !isNaN(coord[1]));
        if (finalValid.length > 0) {
          routeLayerRef.current = L.polyline(finalValid, { color: '#3B82F6', weight: 4, opacity: 0.8, smoothFactor: 1 }).addTo(map);
          finalValid.forEach(coord => coordinates.push(coord));
          routeLayerRef.current.bindPopup(`<div class="text-center"><div class="font-bold text-blue-600 mb-2">🛣️ Trip Route</div><div class="text-sm text-gray-600 mb-1">${finalValid.length} route points</div>${tripDetails.finalDistance ? `<div class="text-sm text-green-600">📏 Distance: ${tripDetails.finalDistance.toFixed(2)} km</div>` : ''}${tripDetails.finalDuration ? `<div class="text-sm text-orange-600">⏱️ Duration: ${tripDetails.finalDuration} mins</div>` : ''}<div class="text-xs text-blue-500 mt-1">Click anywhere on route</div></div>`);
          routeLayerRef.current.on('click', function(e) { this.openPopup(e.latlng); });
        }
      } catch (error) {
        console.warn('Failed to decode polyline:', error);
      }
    }

    // Fit map (will be adjusted by driver location effect if needed).
    // Only the first time for a trip, and once more when its route line first appears.
    // A live route is redrawn every few seconds; re-fitting each time threw away
    // whatever the viewer had zoomed or dragged to.
    const fitKey = `${tripDetails?._id || ''}:${hasEncodedPolyline ? 'line' : 'markers'}`;
    fitKeyRef.current = fitKey;
    lastCoordsRef.current = coordinates;
    // Counts as done only when it actually ran against a real container size; otherwise the
    // resize watcher above redoes it as soon as the container has one.
    if (coordinates.length > 0 && fittedForRef.current !== fitKey && fitToCoords(map, coordinates)) {
      fittedForRef.current = fitKey;
      lastFitAtRef.current = Date.now();
    }
  }, [tripDetails, hasStartLocation, hasEndLocation, hasStops, hasEncodedPolyline]);

  // Separate effect to handle driver location updates independently
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove existing driver marker if any
    if (driverMarkerRef.current) {
      try {
        map.removeLayer(driverMarkerRef.current);
      } catch (e) {
        // Marker might have been removed already
      }
      driverMarkerRef.current = null;
    }

    // Add new driver marker if location available
    if (hasDriverLocation && driverLocation?.coordinates) {
      const [lngRaw, latRaw] = driverLocation.coordinates;
      const lng = Number(lngRaw);
      const lat = Number(latRaw);
      
      if (!Number.isNaN(lng) && !Number.isNaN(lat)) {
        // Format the date/time for display
        const formattedDate = driverLocation.lastUpdated
        ? new Date(driverLocation.lastUpdated).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          })
        : '';
        
        const driverIconHtml = `<div style="background-color: #8B5CF6; width: 28px; height: 28px; border-radius: 50%; border: 4px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display:flex;align-items:center;justify-content:center;font-size:16px;">🚗</div>`;
        const driverIcon = L.divIcon({
          html: driverIconHtml,
          className: 'custom-marker',
          iconSize: [28, 28],
          iconAnchor: [14, 14]
        });
        
        const driverMarker = L.marker([lat, lng], { icon: driverIcon });
        driverMarker.bindPopup(`<div class="text-center"><div class="font-bold text-purple-600">🚗 Driver Location</div><div class="text-sm font-medium">${driverLocation.name || 'Driver'}</div>${formattedDate ? `<div class="text-xs text-gray-600">${formattedDate}</div>` : ''}<div class="text-xs text-gray-500 mt-1">${lat.toFixed(6)}, ${lng.toFixed(6)}</div><div class="text-xs text-green-600 mt-1">Live Location</div></div>`);
        
        driverMarkerRef.current = driverMarker;
        driverMarker.addTo(map);
      }
    }
  }, [driverLocation, hasDriverLocation]);

  // Early return after hooks
  if (!hasLocationData) {
    return (
      <div className="bg-gray-100 rounded-lg flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <p className="text-gray-600">No route data available</p>
          <p className="text-sm text-gray-500 mt-2">
            Trip route information is not available
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height, width: '100%' }}>
      {/* Map Container */}
      <div className="relative bg-gray-200 rounded-lg overflow-hidden" style={{ height: '600px' }}>
        <div ref={mapRef} style={{ height: '100%', width: '100%' }}></div>
        
        {/* Refresh Driver Location Button */}
        {hasDriverLocation && onRefreshDriver && (
          <button
            onClick={onRefreshDriver}
            className="absolute top-4 right-4 bg-gray-600 hover:bg-gray-700 text-white rounded-lg shadow-lg p-2 transition-colors"
            title="Refresh Driver Location"
            style={{ zIndex: 9999, pointerEvents: 'auto' }}
          >
            Refresh Driver
            {/* <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.3c56 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg> */}
          </button>
        )}
        
        {/* Route Overlay - Fixed positioning */}
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-4 max-w-xs" style={{ zIndex: 9999, pointerEvents: 'auto' }}>
          <h4 className="font-medium text-gray-900 mb-3">Trip Route</h4>
          <div className="space-y-2 text-sm">
            {/* Start Location */}
            {hasStartLocation && (
              <div className="flex items-start space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full mt-1 flex-shrink-0"></div>
                <div className="min-w-0 flex-1">
                  <span className="text-gray-600">Start:</span>
                  <div className="font-medium text-xs break-words">
                    {Array.isArray(tripDetails.startLocation) 
                      ? `${tripDetails.startLocation[0].toFixed(6)}, ${tripDetails.startLocation[1].toFixed(6)}`
                      : tripDetails.startLocation}
                  </div>
                </div>
              </div>
            )}
            
            {/* Stops */}
            {hasStops && tripDetails.stops.map((stop, index) => (
              <div key={index} className="flex items-start space-x-2">
                <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${
                  stop.name === 'Pickup Point' ? 'bg-blue-500' :
                  stop.name === 'Drop Point' ? 'bg-red-500' :
                  'bg-yellow-500'
                }`}></div>
                <div className="min-w-0 flex-1">
                  <span className="text-gray-600">{stop.name}:</span>
                  <div className="font-medium text-xs break-words">
                    {stop.location ? `${stop.location[0].toFixed(6)}, ${stop.location[1].toFixed(6)}` : 'N/A'}
                  </div>
                </div>
              </div>
            ))}
            
            {/* End Location */}
            {hasEndLocation && (
              <div className="flex items-start space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full mt-1 flex-shrink-0"></div>
                <div className="min-w-0 flex-1">
                  <span className="text-gray-600">End:</span>
                  <div className="font-medium text-xs break-words">
                    {Array.isArray(tripDetails.endLocation) 
                      ? `${tripDetails.endLocation[0].toFixed(6)}, ${tripDetails.endLocation[1].toFixed(6)}`
                      : tripDetails.endLocation}
                  </div>
                </div>
              </div>
            )}
            
            {/* Driver Location */}
            {hasDriverLocation && (
              <div className="flex items-start space-x-2 pt-2 border-t border-gray-200">
                <div className="w-3 h-3 bg-purple-500 rounded-full mt-1 flex-shrink-0"></div>
                <div className="min-w-0 flex-1">
                  <span className="text-gray-600">🚗 Driver (Live):</span>
                  <div className="font-medium text-xs break-words">
                    {driverLocation.coordinates[1].toFixed(6)}, {driverLocation.coordinates[0].toFixed(6)}
                  </div>
                </div>
              </div>
            )}
            
            {/* Trip Stats */}
            {tripDetails.finalDistance && (
              <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span className="text-gray-600">Distance:</span>
                <span className="font-medium">{tripDetails.finalDistance.toFixed(2)} km</span>
              </div>
            )}
            {tripDetails.finalDuration && (
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-600">Duration:</span>
                <span className="font-medium">{tripDetails.finalDuration} mins</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripMap; 